/**
 * WhatsAppChatPickerBanner — Global persistent banner that nudges users to
 * finish their WhatsApp setup by picking which chats N71 should import.
 *
 * Why this exists:
 *   After QR scan, a user's Baileys session becomes "chats ready" — the
 *   bridge has loaded their chat list but N71 is waiting for them to choose
 *   which conversations to backfill into the KG. Without this step the
 *   whole WhatsApp integration produces zero value (no messages, no
 *   contacts, no KG enrichment).
 *
 *   Previously the only way to trigger the chat picker was a small button
 *   buried on /integrations. Kareem Anabtawi (VVIP) hit this failure mode
 *   on day 1 — stayed on the dashboard after scanning, never navigated to
 *   /integrations, never saw the trigger, sat in "connected but 0 messages"
 *   for 4+ days. This banner fixes that.
 *
 * Behavior:
 *   - Shows whenever context.bannerEligibleSync is set (chats ready, not
 *     mid-sync, not dismissed/opted-out, snooze expired)
 *   - Self-hides on /onboarding/* routes so it doesn't stack with the
 *     onboarding chrome
 *   - Primary CTA opens the existing WhatsAppChatSelectorModal via
 *     openChatSelector(sessionId)
 *   - Snooze menu offers "Remind me in 7 days" or "Don't ask again"
 *     (the latter stores 'opted_out' — user can still reach the picker
 *     via /integrations → "Select chats →" fallback)
 */

import { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, X, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useWhatsAppSync } from '@/contexts/WhatsAppSyncContext';

export function WhatsAppChatPickerBanner() {
  const { bannerEligibleSync, openChatSelector, snoozeBanner, optOutBanner } = useWhatsAppSync();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close the dismiss menu when clicking outside
  useEffect(() => {
    if (!menuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  // Hide on onboarding routes — they have their own WA flow and the banner
  // would stack awkwardly on top of the onboarding header.
  if (location.pathname.startsWith('/onboarding')) return null;
  if (!bannerEligibleSync) return null;

  const sync = bannerEligibleSync;
  const chatCount = sync.chatCount;

  const handlePickChats = () => {
    openChatSelector(sync.sessionId);
  };

  const handleSnooze = () => {
    snoozeBanner(sync.sessionId, 7);
    setMenuOpen(false);
  };

  const handleOptOut = () => {
    optOutBanner(sync.sessionId);
    setMenuOpen(false);
  };

  return (
    <AnimatePresence>
      <motion.div
        key="wa-chat-picker-banner"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        transition={{ duration: 0.2 }}
        className={cn(
          "fixed top-0 left-0 right-0 z-50",
          "bg-[#25D366]/[0.08] backdrop-blur-md",
          "border-b border-[#25D366]/20",
        )}
      >
        <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center gap-4">
          {/* Icon + copy */}
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="w-7 h-7 rounded-full bg-[#25D366]/15 flex items-center justify-center flex-shrink-0">
              <Phone className="w-3.5 h-3.5 text-[#25D366]" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground">
                Pick your WhatsApp chats to finish setup
                {chatCount ? (
                  <span className="text-foreground/50 font-normal ml-1.5">
                    · {chatCount} available
                  </span>
                ) : null}
              </p>
              <p className="hidden md:block text-xs text-foreground/50 truncate">
                Choose which conversations N71 should import. You can change this anytime.
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={handlePickChats}
              className={cn(
                "text-xs font-medium px-3 py-1.5 rounded-md",
                "bg-[#25D366] text-white hover:bg-[#1fb559] transition-colors",
                "whitespace-nowrap",
              )}
            >
              Pick chats →
            </button>

            {/* Dismiss menu */}
            <div ref={menuRef} className="relative">
              <button
                onClick={() => setMenuOpen(v => !v)}
                className={cn(
                  "text-xs text-foreground/50 hover:text-foreground/80 transition-colors",
                  "px-2 py-1.5 rounded-md hover:bg-foreground/[0.04]",
                  "flex items-center gap-1",
                )}
                aria-label="Dismiss options"
                title="Dismiss"
              >
                <X className="w-3.5 h-3.5" />
                <ChevronDown className="w-3 h-3 opacity-60" />
              </button>
              {menuOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={cn(
                    "absolute right-0 mt-1 min-w-[180px]",
                    "bg-background border border-foreground/10 rounded-lg shadow-lg",
                    "overflow-hidden z-10",
                  )}
                >
                  <button
                    onClick={handleSnooze}
                    className="w-full text-left px-3 py-2 text-xs text-foreground/80 hover:bg-foreground/[0.04] transition-colors"
                  >
                    Remind me in 7 days
                  </button>
                  <button
                    onClick={handleOptOut}
                    className="w-full text-left px-3 py-2 text-xs text-foreground/60 hover:bg-foreground/[0.04] transition-colors border-t border-foreground/[0.06]"
                  >
                    Don't ask again
                  </button>
                </motion.div>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
